import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import {
  Users, Wallet, Settings as SettingsIcon, TrendingUp, Plus, Edit2,
  Copy, X, Loader2, CheckCircle2, DollarSign, FileText, Ban, Search, Trash2,
  AlertCircle, Download,
} from 'lucide-react';


function KpiCard({ icon: Icon, label, value, sub, color = '#D44E20', testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}18` }}>
          <Icon size={16} style={{ color }} />
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      </div>
      <p className="text-2xl font-bold text-[#1A1A1A]">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}


/* ── Agent create/edit modal ──────────────────────────────────────────────── */
function AgentModal({ agent, onClose, onSaved }) {
  const editing = !!agent;
  const [form, setForm] = useState({
    name: agent?.name || '', email: agent?.email || '', password: '',
    phone: agent?.phone || '', tax_regime: agent?.tax_regime || 'ordinario',
    vat_number: agent?.vat_number || '', tax_code: agent?.tax_code || '',
    iban: agent?.iban || '', address: agent?.address || '',
    upfront_amount: agent?.upfront_amount ?? '', recurring_rate: agent?.recurring_rate ?? '',
    payout_threshold: agent?.payout_threshold ?? '', notes: agent?.notes || '',
    status: agent?.status || 'active',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await adminApi.put(`/superadmin/agents/${agent.id}`, payload);
      } else {
        await adminApi.post('/superadmin/agents', form);
      }
      toast.success(editing ? 'Agente aggiornato' : 'Agente creato');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
    setSaving(false);
  };

  const field = (key, label, type = 'text', placeholder = '') => (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      <input type={type} value={form[key] ?? ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder} data-testid={`agent-field-${key}`}
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]" />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="agent-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-lg font-bold text-[#1A1A1A]">{editing ? `Modifica: ${agent.name}` : 'Nuovo agente commerciale'}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            {field('name', 'Nome completo*', 'text', 'Marco Rossi')}
            {field('email', 'Email*', 'email', 'marco@email.it')}
            {field('password', editing ? 'Nuova password (opzionale)' : 'Password*', 'password')}
            {field('phone', 'Telefono', 'tel', '+39 333 ...')}
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Dati fiscali</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Regime fiscale</label>
                <select value={form.tax_regime} onChange={(e) => setForm({ ...form, tax_regime: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]">
                  <option value="ordinario">Ordinario (P.IVA)</option>
                  <option value="forfettario">Forfettario</option>
                  <option value="occasional">Prestazione occasionale (max 5000€/anno)</option>
                </select>
              </div>
              {field('vat_number', 'P.IVA', 'text', 'IT12345678901')}
              {field('tax_code', 'Codice fiscale', 'text')}
              {field('iban', 'IBAN', 'text', 'IT60X0542811101000000123456')}
            </div>
            <div className="mt-3">{field('address', 'Indirizzo', 'text')}</div>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Commissioni personalizzate (vuoto = default)</p>
            <div className="grid grid-cols-3 gap-4">
              {field('upfront_amount', 'Upfront € (primo pagamento)', 'number')}
              {field('recurring_rate', '% ricorrente', 'number')}
              {field('payout_threshold', 'Soglia payout €', 'number')}
            </div>
          </div>
          {editing && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Stato</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="agent-field-status"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]">
                <option value="active">Attivo</option>
                <option value="suspended">Sospeso</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Note interne</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-semibold hover:bg-white">Annulla</button>
          <button onClick={save} disabled={saving} data-testid="save-agent"
            className="px-5 py-2 bg-[#D44E20] text-white rounded-lg text-sm font-semibold hover:shadow-md disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : editing ? 'Salva modifiche' : 'Crea agente'}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── Payout approve/pay modal ─────────────────────────────────────────────── */
function PayoutActionModal({ payout, action, onClose, onDone }) {
  const [transactionRef, setTransactionRef] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      if (action === 'approve') {
        await adminApi.post(`/superadmin/payouts/${payout.id}/approve`);
      } else if (action === 'mark-paid') {
        await adminApi.post(`/superadmin/payouts/${payout.id}/mark-paid`,
          { transaction_ref: transactionRef, payment_date: paymentDate });
      } else if (action === 'reject') {
        await adminApi.post(`/superadmin/payouts/${payout.id}/reject`, { reason });
      }
      toast.success('Operazione completata');
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
    setSaving(false);
  };

  const titles = { approve: 'Approva payout', 'mark-paid': 'Marca come pagato', reject: 'Rifiuta payout' };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-lg font-bold text-[#1A1A1A]">{titles[action]}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
            <p><b>Agente:</b> {payout.agent_name}</p>
            <p><b>Totale:</b> {Number(payout.total_amount || 0).toFixed(2)} €</p>
            <p><b>IBAN:</b> {payout.iban_snapshot || '—'}</p>
            <p><b>P.IVA:</b> {payout.vat_snapshot || '—'}</p>
            <p><b>Fattura:</b> {payout.invoice_number || '—'}</p>
          </div>
          {action === 'approve' && (
            <p className="text-sm text-slate-700">Confermi l'approvazione? Dopo questo passaggio puoi procedere al bonifico bancario e marcare come pagato.</p>
          )}
          {action === 'mark-paid' && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Data pagamento*</label>
                <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Riferimento bonifico (CRO/TRN)</label>
                <input type="text" value={transactionRef} onChange={e => setTransactionRef(e.target.value)}
                  placeholder="Es. 202603120000001234" data-testid="payout-transaction-ref"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
            </>
          )}
          {action === 'reject' && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Motivo rifiuto*</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
                data-testid="payout-reject-reason"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-semibold">Annulla</button>
          <button onClick={submit} disabled={saving} data-testid="payout-action-confirm"
            className="px-4 py-2 bg-[#D44E20] text-white rounded-lg text-sm font-semibold hover:shadow-md disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Conferma'}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── MAIN PAGE ─────────────────────────────────────────────────────────────── */
export default function SuperadminAgents() {
  const [tab, setTab] = useState('overview');

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="superadmin-agents-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1A1A1A]">Agenti commerciali</h1>
        <p className="text-sm text-slate-500 mt-1">Gestisci rete agenti, commissioni e payout.</p>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
        {[
          { k: 'overview', l: 'Overview', i: TrendingUp },
          { k: 'agents', l: 'Agenti', i: Users },
          { k: 'payouts', l: 'Payout', i: Wallet },
          { k: 'config', l: 'Configurazione', i: SettingsIcon },
        ].map(({ k, l, i: Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`agents-tab-${k}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${
              tab === k ? 'border-[#D44E20] text-[#D44E20]' : 'border-transparent text-slate-500 hover:text-[#1A1A1A]'
            }`}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'agents' && <AgentsListTab />}
      {tab === 'payouts' && <PayoutsTab />}
      {tab === 'config' && <ConfigTab />}
    </div>
  );
}


function OverviewTab() {
  const [data, setData] = useState(null);
  useEffect(() => { adminApi.get('/superadmin/agents-overview').then(r => setData(r.data)); }, []);
  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" /></div>;

  return (
    <div data-testid="agents-overview-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard testId="kpi-agents" icon={Users} label="Agenti attivi" value={data.total_agents} color="#D44E20" />
        <KpiCard testId="kpi-referrals" icon={TrendingUp} label="Iscrizioni" value={data.total_referrals} color="#8B5CF6" />
        <KpiCard testId="kpi-earned" icon={DollarSign} label="Da pagare" value={`${data.commissions_total_earned.toFixed(2)} €`} color="#F59E0B" sub={`${data.pending_payouts} payout in attesa`} />
        <KpiCard testId="kpi-paid" icon={CheckCircle2} label="Pagato totale" value={`${data.commissions_total_paid.toFixed(2)} €`} color="#10B981" />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Top 5 agenti</h3>
        {data.top_agents.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">Nessun agente ancora.</p>
        ) : (
          <div className="space-y-2">
            {data.top_agents.map((a, i) => (
              <div key={a.agent_id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                <span className="w-7 h-7 rounded-full bg-[#D44E20] text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold">{a.name}</p>
                  <p className="text-[11px] text-slate-500 font-mono">{a.referral_code} · {a.referrals} iscrizioni</p>
                </div>
                <p className="text-sm font-bold text-[#D44E20]">{a.total.toFixed(2)} €</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function AgentsListTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    adminApi.get('/superadmin/agents').then(r => setList(r.data.agents || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (a) => {
    if (!window.confirm(`Disattivare ${a.name}? La storia commissioni resta.`)) return;
    try {
      await adminApi.delete(`/superadmin/agents/${a.id}`);
      toast.success('Agente disattivato');
      load();
    } catch (e) { toast.error('Errore'); }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/signup?ref=${code}`;
    navigator.clipboard.writeText(url);
    toast.success('Link referral copiato');
  };

  const filtered = list.filter(a =>
    !q || a.name.toLowerCase().includes(q.toLowerCase()) || a.email.toLowerCase().includes(q.toLowerCase()) || a.referral_code?.includes(q.toUpperCase()),
  );

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" /></div>;

  return (
    <div data-testid="agents-list-tab">
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca per nome, email o codice..."
            data-testid="agents-search"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D44E20]" />
        </div>
        <button onClick={() => setCreating(true)} data-testid="create-agent-btn"
          className="flex items-center gap-2 px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg hover:shadow-md">
          <Plus size={14} /> Nuovo agente
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <p className="text-center py-10 text-slate-500 text-sm">Nessun agente.</p>
        ) : (
          <table className="w-full text-sm" data-testid="agents-table">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Nome</th>
                <th className="text-left px-4 py-3">Codice</th>
                <th className="text-left px-4 py-3">Commissioni</th>
                <th className="text-right px-4 py-3">Iscrizioni</th>
                <th className="text-right px-4 py-3">Maturato</th>
                <th className="text-right px-4 py-3">Pagato</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-right px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`agent-row-${a.referral_code}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold">{a.name}</p>
                    <p className="text-[11px] text-slate-500">{a.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <code className="text-xs font-mono font-bold">{a.referral_code}</code>
                      <button onClick={() => copyLink(a.referral_code)} title="Copia link referral"
                        data-testid={`copy-link-${a.referral_code}`}
                        className="text-slate-400 hover:text-[#D44E20]">
                        <Copy size={12} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {a.upfront_amount > 0 && <span>{a.upfront_amount}€ upfront</span>}
                    {a.upfront_amount > 0 && a.recurring_rate > 0 && <span> + </span>}
                    {a.recurring_rate > 0 && <span>{a.recurring_rate}%</span>}
                  </td>
                  <td className="px-4 py-3 text-right">{a.referrals_count}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#D44E20]">{a.total_earned.toFixed(2)} €</td>
                  <td className="px-4 py-3 text-right text-slate-600">{a.total_paid.toFixed(2)} €</td>
                  <td className="px-4 py-3">
                    {a.status === 'active' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Attivo</span>}
                    {a.status === 'suspended' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Sospeso</span>}
                    {a.status === 'deleted' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Disattivato</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setEditing(a)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600" title="Modifica"
                        data-testid={`edit-agent-${a.referral_code}`}>
                        <Edit2 size={13} />
                      </button>
                      {a.status !== 'deleted' && (
                        <button onClick={() => remove(a)} className="p-1.5 rounded-md hover:bg-red-50 text-red-500" title="Disattiva">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(creating || editing) && (
        <AgentModal agent={editing} onClose={() => { setEditing(null); setCreating(false); }} onSaved={load} />
      )}
    </div>
  );
}


function PayoutsTab() {
  const [status, setStatus] = useState('pending');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // {payout, type}

  const load = () => {
    setLoading(true);
    adminApi.get(`/superadmin/payouts?status=${status}`).then(r => setList(r.data.payouts || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const downloadInvoice = async (p) => {
    try {
      const r = await adminApi.get(`/superadmin/payouts/${p.id}/invoice-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${p.invoice_number || 'fattura'}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Errore download'); }
  };

  return (
    <div data-testid="payouts-tab">
      <div className="flex items-center gap-1 mb-4">
        {['pending', 'approved', 'paid', 'rejected'].map(s => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`payout-status-${s}`}
            className={`text-xs font-semibold px-3 py-2 rounded-lg ${status === s ? 'bg-[#D44E20] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            {({ pending: 'In attesa', approved: 'Approvati', paid: 'Pagati', rejected: 'Rifiutati' })[s]}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" /></div>
        ) : list.length === 0 ? (
          <p className="text-center py-10 text-slate-500 text-sm">Nessun payout in questo stato.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Agente</th>
                <th className="text-left px-4 py-3">Richiesto</th>
                <th className="text-left px-4 py-3">Fattura</th>
                <th className="text-right px-4 py-3">Importo</th>
                <th className="text-right px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {list.map(p => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`payout-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold">{p.agent_name}</p>
                    <p className="text-[11px] text-slate-500">{p.agent_vat || '—'} · {p.iban_snapshot || 'No IBAN'}</p>
                  </td>
                  <td className="px-4 py-3 text-xs">{new Date(p.requested_at).toLocaleDateString('it-IT')}</td>
                  <td className="px-4 py-3">
                    {p.invoice_pdf_path ? (
                      <button onClick={() => downloadInvoice(p)} className="text-xs text-[#D44E20] font-semibold flex items-center gap-1 hover:underline">
                        <Download size={11} /> {p.invoice_number}
                      </button>
                    ) : <span className="text-xs text-amber-600">In attesa fattura</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-[#D44E20]">{Number(p.total_amount).toFixed(2)} €</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {p.status === 'pending' && p.invoice_pdf_path && (
                        <button onClick={() => setAction({ payout: p, type: 'approve' })}
                          data-testid={`approve-payout-${p.id}`}
                          className="text-xs px-2.5 py-1 rounded-md bg-emerald-100 text-emerald-700 font-semibold hover:bg-emerald-200">Approva</button>
                      )}
                      {p.status === 'approved' && (
                        <button onClick={() => setAction({ payout: p, type: 'mark-paid' })}
                          data-testid={`mark-paid-${p.id}`}
                          className="text-xs px-2.5 py-1 rounded-md bg-[#D44E20] text-white font-semibold hover:shadow">Marca pagato</button>
                      )}
                      {(p.status === 'pending' || p.status === 'approved') && (
                        <button onClick={() => setAction({ payout: p, type: 'reject' })}
                          className="text-xs px-2.5 py-1 rounded-md bg-red-50 text-red-600 font-semibold hover:bg-red-100">Rifiuta</button>
                      )}
                      {p.status === 'paid' && <span className="text-[11px] font-bold text-emerald-600">✓ Pagato</span>}
                      {p.status === 'rejected' && <span className="text-[11px] font-bold text-red-600">✗ Rifiutato</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {action && <PayoutActionModal payout={action.payout} action={action.type} onClose={() => setAction(null)} onDone={load} />}
    </div>
  );
}


function ConfigTab() {
  const [cfg, setCfg] = useState({ default_upfront: 50, default_rate: 15, default_threshold: 100, referral_cookie_days: 30 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get('/superadmin/agents-config').then(r => setCfg(r.data)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put('/superadmin/agents-config', cfg);
      toast.success('Configurazione salvata');
    } catch (e) { toast.error('Errore'); }
    setSaving(false);
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" /></div>;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="agents-config-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-lg font-bold text-[#1A1A1A]">Valori di default per nuovi agenti</h3>
          <p className="text-xs text-slate-500 mt-1">Questi valori saranno pre-compilati quando crei un nuovo agente (modificabili per ognuno).</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-agents-config"
          className="px-4 py-2 bg-[#D44E20] text-white text-sm font-semibold rounded-lg disabled:opacity-50 flex items-center gap-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : 'Salva'}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Upfront € (primo pagamento)</label>
          <input type="number" value={cfg.default_upfront} onChange={e => setCfg({ ...cfg, default_upfront: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">% ricorrente</label>
          <input type="number" value={cfg.default_rate} onChange={e => setCfg({ ...cfg, default_rate: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Soglia payout €</label>
          <input type="number" value={cfg.default_threshold} onChange={e => setCfg({ ...cfg, default_threshold: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Cookie referral (giorni)</label>
          <input type="number" value={cfg.referral_cookie_days} onChange={e => setCfg({ ...cfg, referral_cookie_days: parseInt(e.target.value) || 30 })}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
      </div>
    </div>
  );
}
